/* Custom Input Component to be used with Formik */
import React, { useMemo } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text, FormErrorMessage, FormFieldInfo, Spacer
} from 'components/atoms';
import Select from 'react-select';
import Creatable from 'react-select/creatable';
import { darken } from 'polished';
import { useAppContext, actions } from 'context/AppContext'
import { useFieldError } from 'hooks'

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

export const getLabelFromOptions = (options, value) => {
  const option = options.find(opt => {
    if (opt.label && (opt.value || opt.value === 0 || opt.value === '')) {
      return opt.value === value
    }
    return opt === value
  })
  if (option && (option.label || option.label === 0 || option.label === '')) return option.label
  return value
}

const FieldSelect = ({
  title,
  options,
  field,
  creatable,
  isMulti,
  form: {
    errors, setFieldValue, touched, submitCount, setFieldTouched
  },
  info,
  ...props
}) => {
  const [, dispatch] = useAppContext();

  const isError = useFieldError({
    touched, errors, field, submitCount
  })

  const SelectComponent = useMemo(() => (creatable ? Creatable : Select), [
    creatable,
  ]);

  const handleChange = option => {
    if (isMulti) {
      const values = option ? option.map(({ value }) => value) : [];
      setFieldValue(field.name, values);
      return;
    }
    if (option.value || option.value === '' || option.value === 0) setFieldValue(field.name, option.value);
  };

  // handleBlur and handleFocus enables field select to be used inside modals
  // due to the portal the click in the select would close the modal
  const handleBlur = (e) => {
    if (field && field.onBlur && typeof field.onBlur === 'function') {
      field.onBlur(e)
      setFieldTouched(field.name, true)
    }
    dispatch({
      type: actions.MODAL_SET_SHOULD_CLOSE_ON_CLICK_OUTSIDE,
      data: true
    })
  }

  const handleFocus = (e) => {
    if (field && field.onFocus && typeof field.onFocus === 'function') field.onFocus(e)
    dispatch({
      type: actions.MODAL_SET_SHOULD_CLOSE_ON_CLICK_OUTSIDE,
      data: false
    })
  }

  const noOptionsMessage = useMemo(() => {
    if (creatable) return 'Escreva e pressione \'enter\' ↵';
    return 'Sem opções';
  }, [creatable]);

  const valuesComputed = useMemo(() => {
    if (Array.isArray(field.value) && field.value.length > 0) {
      return field.value.map(value => ({
        label: getLabelFromOptions(options, value),
        value
      }))
    }
    if (
      !(field.value instanceof Array)
      && (field.value || field.value === 0 || field.value === '')
    ) {
      return {
        label: getLabelFromOptions(options, field.value),
        value: field.value
      }
    }
    return null
  }, [field.value, options])

  return (
    <FieldSelectStyles isMulti={isMulti}>
      <Top>{title && <Text modifiers={['base']}>{title}</Text>}</Top>
      <SelectComponent
        classNamePrefix="react-select"
        noOptionsMessage={() => noOptionsMessage}
        options={options}
        name={field.name}
        value={valuesComputed}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        isMulti={isMulti}
        menuPortalTarget={document.getElementById('portal')}
        formatCreateLabel={(value) => (
          <TagLabel>
            <Text modifiers={['light']}>pressione &apos;enter&apos; para adicionar a opção:</Text>
            <Spacer left="xs3">
              <Text modifiers={['xbase']}>
                {value}
              </Text>
            </Spacer>
          </TagLabel>
        )}
        {...props}
      />
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </FieldSelectStyles>
  );
};

FieldSelect.propTypes = {
  title: PropTypes.string,
  creatable: PropTypes.bool,
  isMulti: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isValid: PropTypes.bool,
    setFieldValue: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
};

FieldSelect.defaultProps = {
  title: null,
  options: [],
  creatable: false,
  isMulti: false,
  info: null,
};

const Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FieldSelectStyles = styled.div`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }

  .react-select {
    &-optimized-option {
      &:hover {
        background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
      }
    }
    &__indicator-separator {
      padding: 0;
      margin-right: ${props => props.theme.spacing.xs3};
      ${props => !props.isMulti && 'display:none;'};
    }
    &__control {
      cursor: pointer;
      background-color: transparent;
      color: ${props => props.theme.colors.base};
      font-size: ${props => props.theme.sizes.s};
      font-weight: ${props => props.theme.weight.light};
      width: 100%;
      border: 0;
      border-radius: 0;
      border-bottom: ${props => props.theme.values.borderSize} solid
        ${props => props.theme.colors.base};
      &:hover {
        border-color: ${props => props.theme.colors.base};
      }
      &--is-focused {
        border: 0;
        border-bottom: ${props => props.theme.values.borderSize} solid
          ${props => props.theme.colors.base};
        box-shadow: none;
      }
    }

    &__dropdown-indicator {
      padding: ${props => `${props.theme.spacing.xs2} 0`};
    }

    &__value-container {
      padding: ${props => `${props.theme.spacing.xs2} 0`};
    }

    &__menu {
      border-radius: 0;
      box-shadow: none;
      &-notice {
        font-size: ${props => props.theme.sizes.s};
        font-weight: ${props => props.theme.weight.light};
      }
    }
    &__option {
      font-size: ${props => props.theme.sizes.s};
      background-color: ${props => props.theme.colors.bg};
      color: ${props => props.theme.colors.base};
      &--is-selected {
        background-color: ${props => props.theme.colors.bgDark} !important;
        color: ${props => props.theme.colors.baseInverse} !important;
      }
      &--is-focused {
        background-color: ${props => darken(0.04, props.theme.colors.bgLighter)};
        color: ${props => props.theme.colors.base};
      }
    }

    &__indicator {
      color: ${props => props.theme.colors.base};
    }

    &__multi-value {
      background-color: ${props => props.theme.colors.greyLighS};
      border-radius: ${props => props.theme.sizes.xs};
      &__label {
        font-size: ${props => props.theme.sizes.s};
      }
    }
  }
`;

const TagLabel = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`

export default FieldSelect;
